import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { MemberFormData } from "../../organisms/form/MemberForm";
import { TableHeaderItem } from "../../../hooks/useTable";
import { USER_ROLE } from "../../../constants/app";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../store";
import { Member, memberActions, memberSelectors } from "../../../ducks/member";
import { appActions } from "../../../ducks/app";
import { authSelectors } from "../../../ducks/auth";
import { ToastType } from "../../atoms/Toast";
import { MemberCreateRequestBody } from "../../../core/api/requests/member/create/types";
import { MemberEditRequestBody } from "../../../core/api/requests/member/edit/types";
import { formatLastLogin } from "../../../utils/date";
import { useModalWithProtection } from "../../../hooks/useModalWithProtection";
import { getCurrentUserFromStorage } from "../../../utils/auth";

const roleLabel = (role: USER_ROLE) => {
  switch (role) {
    case USER_ROLE.ADMIN:
      return "Admin";
    case USER_ROLE.MANAGER:
      return "Manager";
    case USER_ROLE.ASSISTANT:
      return "Assistant";
    case USER_ROLE.NORMAL_USER:
      return "User";
    default:
      return role;
  }
};

export const useMemberToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    visible: boolean;
  }>({ message: "", type: "success", visible: false });

  const showToast = (message: string, type: ToastType = "success") => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 2000);
  };

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  return { toast, showToast, hideToast };
};

export const useMemberPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { toast, showToast, hideToast } = useMemberToast();

  const members = useSelector(memberSelectors.members);

  const refetchMembers = useCallback(() => {
    dispatch(memberActions.fetchMembers.started({}));
  }, [dispatch]);

  // Single useEffect to fetch members on mount only
  useEffect(() => {
    dispatch(memberActions.fetchMembers.started({}));
  }, [dispatch]);

  // Get current logged-in user
  const loggedInUser = useSelector(authSelectors.loggedInUser);

  // Add sequential 'no' field for display and map to table row shape
  const membersWithNo = (members ?? []).map((item, idx) => {
    // Compare by email as it's more reliable than ID
    const isCurrentUser = loggedInUser && loggedInUser.email === item.email;
    return {
      ...item, // Keep the original Member properties
      name: isCurrentUser ? `${item.username} (You)` : item.username,
      email: item.email,
      userRole: item.role as unknown as USER_ROLE,
      last_login: formatLastLogin(item.last_login),
      no: idx + 1,
      disabled: isCurrentUser,
    };
  });

  const handleRowDelete = useCallback(
    (data: Member) => {
      dispatch(
        appActions.showModal({
          title: "Delete Confirmation",
          subTitle: "Are you sure you want to delete?",
          buttons: [
            {
              text: "Cancel",
              variant: "border",
              onClick: () => dispatch(appActions.hideModal()),
            },
            {
              text: "Delete",
              variant: "filled",
              onClick: () => {
                dispatch(appActions.hideModal());
                dispatch(appActions.showLoading());
                dispatch(
                  memberActions.deleteMember.started({
                    id: data.id,
                    onComplete: () => {
                      dispatch(appActions.hideLoading());
                      // Always refetch to ensure table shows actual state from server
                      dispatch(memberActions.fetchMembers.started({}));
                    },
                    onSuccess: () => {
                      showToast("Member deleted successfully!", "success");
                    },
                    onError: () => {
                      showToast(
                        "Failed to delete member. Please try again.",
                        "error"
                      );
                    },
                  })
                );
              },
            },
          ],
        })
      );
    },
    [dispatch, showToast]
  );

  // handle modal
  const { isOpen: isCreateModalOpen, openModal: handleOpenCreateModal, closeModal: handleCloseCreateModal } = useModalWithProtection();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const handleOpenEditModal = () => setIsEditModalOpen(true);
  const handleCloseEditModal = () => setIsEditModalOpen(false);



  // const [members, setMembers] = useState<(MemberFormData & { no: number })[]>([]);
  const [loading, setLoading] = useState(false);

  // const fetchData = async () => {
  //     setLoading(true);
  //     try {
  //         const response = await memberListApi.call({});
  //         const data: MemberListResponse = response.data;
  //         const mapped = data.map((item, idx) => ({
  //             name: item.username,
  //             email: item.email,
  //             userRole: item.role as unknown as USER_ROLE,
  //             no: idx + 1,
  //         }));
  //         setMembers(mapped);
  //     } catch (e) {
  //         // Optionally handle error
  //         setMembers([]);
  //     } finally {
  //         setLoading(false);
  //     }
  // };

  // useEffect(() => {
  //     fetchData();
  // }, []);

  const tableHeaders = useMemo(
    () => [
      { id: 1, title: "No", dataKey: "no" },
      { id: 2, title: "Name", dataKey: "name" },
      { id: 3, title: "Email", dataKey: "email" },
      { id: 4, title: "Role", dataKey: "userRole" },
      { id: 5, title: "Last Login", dataKey: "last_login" },
    ],
    []
  );

  return {
    tableHeaders,
    members: membersWithNo,
    loading,
    handleRowDelete,
    selectedMember,
    toast,
    showToast,
    hideToast,
    fetchData: () => { },
    refetchMembers,
    isCreateModalOpen,
    isEditModalOpen,
    handleCloseCreateModal,
    handleOpenCreateModal,
    handleCloseEditModal,
    handleOpenEditModal,
  };
};

export const useMemberRegistration = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchMembers?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittingRef = useRef(false);

  const handleRegisterMember = useCallback(
    (
      data: MemberCreateRequestBody,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;

      dispatch(
        memberActions.createNewMember.started({
          ...data,
          onComplete: () => {
            isSubmittingRef.current = false;
          },
          onSuccess: (result: any) => {
            isSubmittingRef.current = false;
            showToast("Member created successfully!", "success");
            setTimeout(() => {
              if (refetchMembers) refetchMembers();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (err: any) => {
            isSubmittingRef.current = false;

            // Extract user-friendly error message from backend response
            let message = "Failed to create member. Please try again.";

            if (err?.response?.data) {
              const data = err.response.data;
              if (typeof data === 'string') {
                message = data;
              } else if (typeof data === 'object') {
                // Handle field-specific errors (e.g., {"email": ["user with this email already exists."]})
                if (data.email) {
                  const emailErrors = Array.isArray(data.email) ? data.email : [data.email];
                  // Map common Django error messages to user-friendly ones
                  if (emailErrors.some((e: string) => e.toLowerCase().includes('already exists') || e.toLowerCase().includes('unique'))) {
                    message = "This email is already registered. Please use a different email address.";
                  } else {
                    message = emailErrors.join(' ');
                  }
                } else if (data.detail) {
                  message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
                } else if (data.non_field_errors) {
                  message = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(' ') : data.non_field_errors;
                }
              }
            }

            showToast(message, "error");
            if (onError) onError(message);
          },
        })
      );
    },
    [dispatch, showToast, refetchMembers]
  );

  return { handleRegisterMember };
};

export const useMemberEditing = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchMembers?: () => void,
  closeModal?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittingRef = useRef(false);

  const handleEditMember = useCallback(
    (
      data: MemberEditRequestBody,
      id: string,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;

      // Get current user to check if this is a self-update
      const currentUser = getCurrentUserFromStorage();
      const isSelfUpdate = currentUser && currentUser.id === id;

      return dispatch(
        memberActions.updateMember.started({
          ...data,
          id,
          onComplete: () => {
            isSubmittingRef.current = false;
          },
          onSuccess: (result: any) => {
            isSubmittingRef.current = false;

            // Show different message based on whether it's a self-update
            if (isSelfUpdate) {
              showToast("Your account has been updated successfully!", "success");
            } else {
              showToast("Updated successfully!", "success");
            }

            setTimeout(() => {
              if (refetchMembers) refetchMembers();
              if (closeModal) closeModal();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (_err: any) => {
            isSubmittingRef.current = false;
            showToast("Failed to update member. Please try again.", "error");
            if (onError) onError("Failed to update member. Please try again.");
          },
        })
      );
    },
    [dispatch, showToast, refetchMembers, closeModal]
  );

  return { handleEditMember };
};
